import { logWarning } from '@databases/logs';
import {
  type DatabaseField,
  DatabaseFieldType,
  NumberFormats,
  type RawDatabaseValue,
  RenderContext
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';

import { getTextContent } from '../../lib/dom.ts';
import { encodeHtml } from '../../utils.ts';
import type { FieldType } from '../field-types.ts';
import { extractNumber, formatNumber } from './number-format.ts';

export { extractNumber, formatNumber };

export function getNumberDisplayString(
  value: string,
  field: DatabaseField,
  locale: string
) {
  switch (field.settings.numberFormat.format) {
    case NumberFormats.CUSTOM:
      return formatNumber(value, field.settings.numberFormat);
    case NumberFormats.NUMBER: {
      try {
        return formatNumber(value, field.settings.numberFormat);
      } catch (e) {
        if (e instanceof Error) {
          logWarning('Unsupported number format', e);
        }
        return '';
      }
    }
    case NumberFormats.PERCENT:
      return formatNumber(value, {
        ...field.settings.numberFormat,
        suffix: '%'
      });
    case NumberFormats.CURRENCY:
      return formatNumber(value, field.settings.numberFormat);
    case NumberFormats.DOLLAR:
      return Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' }).format(
        Number(value)
      );
    case NumberFormats.EURO:
      return Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(
        Number(value)
      );
  }
}

function getStringRepresentation(context: {
  field: DatabaseField;
  value: RawDatabaseValue<DatabaseFieldType.NUMBER>;
}): string {
  return context.value != null ? `${context.value}` : '';
}

function getNumberRepresentation(context: {
  field: DatabaseField;
  value: RawDatabaseValue<DatabaseFieldType.NUMBER>;
}): number | undefined {
  const extractedNumber = extractNumber(context.value);
  const result = Number(extractedNumber);
  return !isNaN(result) ? result : undefined;
}

const numberFieldType: FieldType<DatabaseFieldType.NUMBER> = {
  supportedFilterTypes: [
    DatabaseFilterType.EMPTY,
    DatabaseFilterType.LESS,
    DatabaseFilterType.GREATER,
    DatabaseFilterType.EQUALS
  ],

  isCsvImportSupported: true,

  renderExportView: ({ value, field }, state, renderContext) => {
    switch (renderContext) {
      case RenderContext.COPY_DATABASE_TO_CLIPBOARD:
      case RenderContext.EXPORT_DATABASE:
        // When exporting the database, do not apply formatting settings, so that the number is properly recognized as a number
        // wherever it is imported/pasted.
        return encodeHtml(getStringRepresentation({ field, value }));

      default:
        return encodeHtml(formatNumber(value, field.settings.numberFormat));
    }
  },

  parseFromHtml: (el) => extractNumber(getTextContent(el)),

  getInitialValue: () => '',

  isEmptyValue: ({ value }) => !value,

  isEqualValue: ({ value: value1 }, { value: value2 }) => value1 === value2,

  getStringRepresentation,

  getNumberRepresentation,

  convertFrom: {
    [DatabaseFieldType.TEXT]: ({ value }) => extractNumber(value.plain)
  },

  convertTo: {
    [DatabaseFieldType.TEXT]: (context) => ({
      plain: getStringRepresentation(context) ?? ''
    })
  }
};

export default numberFieldType;
